import { useState, useEffect } from 'react';
import {
  AlertCircle,
  Plus,
  Search,
  Filter,
  Clock,
  User,
  Building,
  Calendar,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Eye,
  Edit,
  MessageSquare
} from 'lucide-react';
import '../styles/Urgencias.css';

const Urgencias = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState('todas');
  const [themeColor, setThemeColor] = useState('#c4fffc');

  // Actualizar color del tema en tiempo real
  useEffect(() => {
    const updateThemeColor = () => {
      const color = localStorage.getItem('theme-primary-color') || '#c4fffc';
      setThemeColor(color);
    };

    updateThemeColor();
    const interval = setInterval(updateThemeColor, 200);
    window.addEventListener('theme-color-change', updateThemeColor);

    return () => {
      clearInterval(interval);
      window.removeEventListener('theme-color-change', updateThemeColor);
    };
  }, []);

  const urgencias = [
    {
      id: 'URG-2025-001',
      titulo: 'Falla crítica en sistema de bombeo',
      descripcion: 'Sistema de bombeo principal de Minera Escondida presenta falla crítica. Requiere repuestos inmediatos.',
      cliente: 'Minera Escondida',
      responsable: 'Carlos Mendoza',
      prioridad: 'critica',
      estado: 'abierta',
      fechaCreacion: '2025-01-14T08:30:00',
      fechaLimite: '2025-09-16T18:00:00',
      tipoUrgencia: 'tecnica',
      impacto: 'alto',
      cotizacion: 'COT-2025-001',
      orden: 'OV-2025-002'
    },
    {
      id: 'URG-2025-002',
      titulo: 'Retraso en envío de EPP certificado',
      descripcion: 'Envío de equipos de protección personal para Anglo American retrasado por problemas en aduana.',
      cliente: 'Anglo American',
      responsable: 'Miguel Silva',
      prioridad: 'alta',
      estado: 'en-proceso',
      fechaCreacion: '2025-01-13T14:20:00',
      fechaLimite: '2025-09-17T12:00:00',
      tipoUrgencia: 'logistica',
      impacto: 'medio',
      cotizacion: 'COT-2025-003',
      orden: 'OV-2025-004'
    },
    {
      id: 'URG-2025-003',
      titulo: 'Solicitud urgente de válvulas adicionales',
      descripcion: 'BHP Billiton requiere válvulas adicionales para completar instalación. Necesario envío inmediato.',
      cliente: 'BHP Billiton',
      responsable: 'Ana Rodríguez',
      prioridad: 'alta',
      estado: 'abierta',
      fechaCreacion: '2025-01-13T16:45:00',
      fechaLimite: '2025-09-18T09:00:00',
      tipoUrgencia: 'comercial',
      impacto: 'alto',
      cotizacion: 'COT-2025-004',
      orden: 'OV-2025-001'
    },
    {
      id: 'URG-2025-004',
      titulo: 'Problema con certificación ISO',
      descripcion: 'Documentación de certificación ISO para rodamientos presenta inconsistencias. Requiere revisión urgente.',
      cliente: 'Codelco División Andina',
      responsable: 'Roberto Torres',
      prioridad: 'media',
      estado: 'resuelta',
      fechaCreacion: '2025-01-12T11:15:00',
      fechaLimite: '2025-09-14T17:00:00',
      tipoUrgencia: 'documentacion',
      impacto: 'bajo',
      cotizacion: 'COT-2025-002',
      orden: 'OV-2025-003'
    },
    {
      id: 'URG-2025-005',
      titulo: 'Escalación por demora en pago',
      descripcion: 'Cliente escaló demora en procesamiento de pago. CFO requiere atención inmediata.',
      cliente: 'Antofagasta Minerals',
      responsable: 'Laura González',
      prioridad: 'critica',
      estado: 'en-proceso',
      fechaCreacion: '2025-01-14T09:00:00',
      fechaLimite: '2025-09-15T15:00:00',
      tipoUrgencia: 'financiera',
      impacto: 'alto',
      cotizacion: 'COT-2025-005',
      orden: 'OV-2025-005'
    }
  ];

  const filteredUrgencias = urgencias.filter(urgencia => {
    const matchesSearch =
      urgencia.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      urgencia.cliente.toLowerCase().includes(searchTerm.toLowerCase()) ||
      urgencia.responsable.toLowerCase().includes(searchTerm.toLowerCase()) ||
      urgencia.descripcion.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter =
      filterPriority === 'todas' ||
      urgencia.prioridad === filterPriority;

    return matchesSearch && matchesFilter;
  });

  const getPriorityColor = (prioridad) => {
    switch(prioridad) {
      case 'critica': return 'priority-critical';
      case 'alta': return 'priority-high';
      case 'media': return 'priority-medium';
      case 'baja': return 'priority-low';
      default: return 'priority-medium';
    }
  };

  const getStatusColor = (estado) => {
    switch(estado.toLowerCase().replace(' ', '-')) {
      case 'abierta': return 'status-open';
      case 'en-proceso': return 'status-progress';
      case 'en proceso': return 'status-progress';
      case 'resuelta': return 'status-resolved';
      case 'cerrada': return 'status-closed';
      default: return 'status-open';
    }
  };

  const getTypeIcon = (tipo) => {
    switch(tipo) {
      case 'tecnica': return <AlertTriangle size={16} />;
      case 'logistica': return <Clock size={16} />;
      case 'comercial': return <Building size={16} />;
      case 'financiera': return <AlertCircle size={16} />;
      case 'documentacion': return <MessageSquare size={16} />;
      default: return <AlertCircle size={16} />;
    }
  };

  const getTimeRemaining = (fechaLimite) => {
    const now = new Date();
    const deadline = new Date(fechaLimite);
    const diff = deadline.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));

    if (diff < 0) return 'Vencida';
    if (hours < 1) return 'Menos de 1 hora';
    if (hours < 24) return `${hours} horas`;
    const days = Math.floor(hours / 24);
    return `${days} días`;
  };

  const isOverdue = (fechaLimite) => {
    return new Date() > new Date(fechaLimite);
  };

  return (
    <div className="urgencias-container">
      <div className="urgencias-header">
        <div className="header-title">
          <AlertCircle size={28} />
          <h1>Urgencias</h1>
        </div>
        <button className="btn-nueva-urgencia" style={{
          backgroundColor: themeColor,
          color: '#000',
          border: '2px solid #000',
          padding: '10px 20px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '14px',
          fontWeight: '600',
          cursor: 'pointer',
          fontFamily: "'Space Grotesk', sans-serif"
        }}>
          <Plus size={20} />
          Nueva Urgencia
        </button>
      </div>

      <div className="urgencias-stats">
        <div className="stat-card">
          <div className="stat-icon critical" style={{
            width: '48px',
            height: '48px',
            backgroundColor: '#FFB3BA',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid #000'
          }}>
            <AlertCircle size={20} color="#000" />
          </div>
          <div className="stat-content">
            <p>Críticas</p>
            <h3>{urgencias.filter(u => u.prioridad === 'critica' && u.estado !== 'resuelta').length}</h3>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon high" style={{
            width: '48px',
            height: '48px',
            backgroundColor: '#FFDAB9',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid #000'
          }}>
            <AlertTriangle size={20} color="#000" />
          </div>
          <div className="stat-content">
            <p>Alta Prioridad</p>
            <h3>{urgencias.filter(u => u.prioridad === 'alta' && u.estado !== 'resuelta').length}</h3>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon warning" style={{
            width: '48px',
            height: '48px',
            backgroundColor: '#FFFACD',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid #000'
          }}>
            <Clock size={20} color="#000" />
          </div>
          <div className="stat-content">
            <p>Vencidas</p>
            <h3>{urgencias.filter(u => isOverdue(u.fechaLimite) && u.estado !== 'resuelta').length}</h3>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon success" style={{
            width: '48px',
            height: '48px',
            backgroundColor: themeColor,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid #000'
          }}>
            <CheckCircle size={20} color="#000" />
          </div>
          <div className="stat-content">
            <p>Resueltas Hoy</p>
            <h3>{urgencias.filter(u => u.estado === 'resuelta' &&
              new Date(u.fechaCreacion).toDateString() === new Date().toDateString()).length}</h3>
          </div>
        </div>
      </div>

      <div className="urgencias-controls">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Buscar por título, cliente o responsable..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="controls-right">
          <select
            className="filter-select"
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
          >
            <option value="todas">Todas las prioridades</option>
            <option value="critica">Críticas</option>
            <option value="alta">Alta</option>
            <option value="media">Media</option>
            <option value="baja">Baja</option>
          </select>
        </div>
      </div>

      <div className="urgencias-grid">
        {filteredUrgencias.map((urgencia) => (
          <div key={urgencia.id} className={`urgencia-card ${urgencia.prioridad === 'critica' ? 'critical' : ''} ${isOverdue(urgencia.fechaLimite) && urgencia.estado !== 'resuelta' ? 'overdue' : ''}`}>
            <div className="urgencia-header">
              <div className="urgencia-type">
                {getTypeIcon(urgencia.tipoUrgencia)}
                <span className="type-label">{urgencia.tipoUrgencia}</span>
              </div>
              <div className="urgencia-priority">
                <span className={`priority ${getPriorityColor(urgencia.prioridad)}`}>
                  {urgencia.prioridad.toUpperCase()}
                </span>
              </div>
            </div>

            <div className="urgencia-content">
              <div className="urgencia-id">
                <strong>{urgencia.id}</strong>
                <span className={`status ${getStatusColor(urgencia.estado)}`}>
                  {urgencia.estado.replace('-', ' ').toUpperCase()}
                </span>
              </div>

              <h3 className="urgencia-title">{urgencia.titulo}</h3>
              <p className="urgencia-description">{urgencia.descripcion}</p>

              <div className="urgencia-details">
                <div className="detail-row">
                  <Building size={14} />
                  <span><strong>Cliente:</strong> {urgencia.cliente}</span>
                </div>
                <div className="detail-row">
                  <User size={14} />
                  <span><strong>Responsable:</strong> {urgencia.responsable}</span>
                </div>
                <div className="detail-row">
                  <Calendar size={14} />
                  <span><strong>Creada:</strong> {new Date(urgencia.fechaCreacion).toLocaleDateString('es-CL')}</span>
                </div>
              </div>

              <div className="urgencia-timeline">
                <div className={`timeline-item ${isOverdue(urgencia.fechaLimite) && urgencia.estado !== 'resuelta' ? 'overdue' : ''}`}>
                  <Clock size={14} />
                  <span>
                    <strong>Límite:</strong> {getTimeRemaining(urgencia.fechaLimite)}
                  </span>
                </div>
              </div>

              <div className="urgencia-references">
                <div className="ref-links">
                  <span className="ref" style={{
                    backgroundColor: themeColor,
                    color: '#000',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '700',
                    fontFamily: "'Space Grotesk', sans-serif",
                    border: '1px solid #000'
                  }}>COT: {urgencia.cotizacion}</span>
                  <span className="ref" style={{
                    backgroundColor: themeColor,
                    color: '#000',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '700',
                    fontFamily: "'Space Grotesk', sans-serif",
                    border: '1px solid #000'
                  }}>OV: {urgencia.orden}</span>
                </div>
              </div>
            </div>

            <div className="urgencia-actions">
              <button className="btn-action" title="Ver detalles" style={{
                padding: '8px',
                backgroundColor: 'white',
                border: '2px solid #000',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = themeColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
              }}>
                <Eye size={16} />
              </button>
              <button className="btn-action" title="Editar" style={{
                padding: '8px',
                backgroundColor: 'white',
                border: '2px solid #000',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = themeColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
              }}>
                <Edit size={16} />
              </button>
              <button className="btn-action btn-resolve" title="Resolver" style={{
                padding: '8px',
                backgroundColor: 'white',
                border: '2px solid #000',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = themeColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
              }}>
                <CheckCircle size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Urgencias;